<script setup lang="ts">
import { ref, computed } from "vue";
import { UiText, UiTabs, UiTabsItem, UiIcon, UiButton, UiAside } from "@shopexperts/shared";
import { useClientLeads, type ClientLeadStatusFilter } from "@/app/composables/leads/useClientLeads";
import ClientLeadCard from "@/app/components/leads/ClientLeadCard.vue";
import LeadSubmissionForm from "@/app/components/LeadSubmissionForm.vue";
import PageHeader from "@/app/components/common/PageHeader.vue";
import SuccessCheckmarkIcon from "@/assets/icons/success-chekmark.svg";


// Tab state
const activeTab = ref<'all' | 'awaiting_match' | 'matched'>('all');

// Status filter based on active tab
const statusFilter = computed<ClientLeadStatusFilter | undefined>(() => {
  if (activeTab.value === 'awaiting_match') return 'awaiting_match';
  if (activeTab.value === 'matched') return 'matched';
  return undefined;
});

// Fetch leads with API filtering
const { leads, isLoading, error, refetch } = useClientLeads(statusFilter);

// Aside state for submitting new leads
const asideOpen = ref(false);
const formKey = ref(0);

function openSubmitLeadAside() {
  formKey.value++; // Reset form state
  asideOpen.value = true;
}

function handleLeadSubmitSuccess() {
  asideOpen.value = false;
  refetch();
}
</script>

<template>
  <main class="flex-1 p-4 md:p-8 overflow-y-auto bg-secondary font-light w-full">
    <PageHeader
      title="My Requests"
      description="Easily manage all your project requests in one place.<br />Stay organized and move your projects forward faster."
    >
      <template #action>
        <UiButton 
          variant="primary" 
          label="Submit a Request" 
          @click="openSubmitLeadAside"
          show-arrow
        >
        </UiButton>
      </template>
    </PageHeader>

    <!-- Tabs -->
    <UiTabs v-model="activeTab" default-value="all">
      <UiTabsItem value="all" label="All Requests">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12">
          <div class="flex flex-col items-center gap-3">
            <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <UiText variant="p" as="p" class="text-gray-500">Loading your leads...</UiText>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load leads
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600 mb-4">
              {{ (error as Error).message || 'An error occurred while fetching your leads' }}
            </UiText>
            <UiButton variant="outline" label="Try Again" @click="refetch" />
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="leads.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4 max-w-md mx-auto">
            <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
              <UiIcon name="package-placeholder" class="w-10 h-10 text-gray-400" />
            </div>
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-2">
                No leads yet
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600 mb-4">
                Submit your first project lead and we'll match you with the best experts for your needs.
              </UiText>
              <UiButton 
                variant="primary" 
                label="Submit Your First Lead" 
                @click="openSubmitLeadAside"
              />
            </div>
          </div>
        </div>

        <!-- Leads List -->
        <div v-else class="flex flex-col gap-4">
          <ClientLeadCard
            v-for="lead in leads"
            :key="lead.id"
            :lead="lead"
          />
        </div>
      </UiTabsItem>

      <UiTabsItem value="awaiting_match" label="Awaiting Match">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12">
          <div class="flex flex-col items-center gap-3">
            <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <UiText variant="p" as="p" class="text-gray-500">Loading your leads...</UiText>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load leads
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600 mb-4">
              {{ (error as Error).message || 'An error occurred while fetching your leads' }}
            </UiText>
            <UiButton variant="outline" label="Try Again" @click="refetch" />
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="leads.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4 max-w-md mx-auto">
            <div class="w-20 h-20 flex items-center justify-center">
              <SuccessCheckmarkIcon class="w-full h-full" />
            </div>
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-2">
                All caught up!
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                All your leads have been matched with experts.
              </UiText>
            </div>
          </div>
        </div>

        <!-- Leads List -->
        <div v-else class="flex flex-col gap-4">
          <ClientLeadCard
            v-for="lead in leads"
            :key="lead.id"
            :lead="lead"
          />
        </div>
      </UiTabsItem>

      <UiTabsItem value="matched" label="Matched">
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-12">
          <div class="flex flex-col items-center gap-3">
            <div class="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <UiText variant="p" as="p" class="text-gray-500">Loading your leads...</UiText>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-12">
          <div class="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <UiIcon name="close" class="w-12 h-12 text-red-500 mx-auto mb-3" />
            <UiText variant="p" as="p" class="text-red-800 font-semibold mb-2">
              Failed to load leads
            </UiText>
            <UiText variant="p-small" as="p" class="text-red-600 mb-4">
              {{ (error as Error).message || 'An error occurred while fetching your leads' }}
            </UiText>
            <UiButton variant="outline" label="Try Again" @click="refetch" />
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="leads.length === 0" class="text-center py-12">
          <div class="flex flex-col items-center gap-4 max-w-md mx-auto">
            <div class="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
              <UiIcon name="user-circle" class="w-10 h-10 text-gray-400" />
            </div>
            <div>
              <UiText variant="h6" as="h6" class="font-semibold mb-2">
                No matched leads yet
              </UiText>
              <UiText variant="p-small" as="p" class="text-gray-600">
                Your leads are being reviewed. You'll see matched leads here once experts are assigned.
              </UiText>
            </div>
          </div>
        </div>

        <!-- Leads List -->
        <div v-else class="flex flex-col gap-4">
          <ClientLeadCard
            v-for="lead in leads"
            :key="lead.id"
            :lead="lead"
          />
        </div>
      </UiTabsItem>
    </UiTabs>

    <!-- Submit Lead Aside -->
    <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
      <div class="p-6 md:p-8 flex flex-col gap-4 max-h-full overflow-y-auto">
        <LeadSubmissionForm 
          :key="formKey"
          :disable-auto-redirect="true"
          @success="handleLeadSubmitSuccess"
        >
          <template #close-button>
            <UiButton variant="ghost" shape="square" @click="asideOpen = false">
              <template #before_label>
                <UiIcon name="close" class="w-6 h-6" />
              </template>
            </UiButton>
          </template>
        </LeadSubmissionForm>
      </div>
    </UiAside>
  </main>
</template>

